import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/en-nz'
import 'dayjs/locale/en-au'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { LicenseInfo } from '@mui/x-license-pro'
import { SnackbarProvider, SnackbarProviderProps } from 'notistack'

import theme from './theme'
import router from './router'
import { QueryClientProvider } from '@tanstack/react-query'
import { isDebugEnabled, logger } from '$utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import { singletonQueryClient } from './utils/queryClient'
import LoadedSnackbar from './components/common/app/LoadedSnackbar'
import { Action } from './utils/snackbar'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(relativeTime)

if (isDebugEnabled()) {
  logger.enable()
}

LicenseInfo.setLicenseKey(
  'cd69c388ff4910b952fcb8bc61ee5503Tz03OTk3OCxFPTE3MzMzNzY0OTgwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
)

const snackbarProviderProps: SnackbarProviderProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  maxSnack: 3,
  autoHideDuration: 6000,
  action: Action,
  preventDuplicate: true,
  hideIconVariant: true,
  Components: {
    loadedSnackbar: LoadedSnackbar,
  },
}

// We defer rendering of the app until the DOM is loaded
// this avoids a race condition where this code ends up
// running before the DOM element we try and render the
// app into is available.
window.addEventListener('DOMContentLoaded', () => {
  const container: HTMLElement | null =
    document.getElementById('react-container')

  if (container) {
    logger.info('Rendering Loaded React app')
    const root = createRoot(container)
    root.render(
      <ThemeProvider theme={theme}>
        <SnackbarProvider {...snackbarProviderProps}>
          <QueryClientProvider client={singletonQueryClient}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={'en-nz'}
            >
              <RouterProvider router={router} />
            </LocalizationProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    )
  }
})
